import type { ReactNode } from 'react'

import { Box, Link, Stack, Typography, useTheme } from '@mui/material'

import { Button, Card } from '@repo/ui'
import logo from 'assets/images/logo.svg'
import { SUPPORT_EMAIL } from 'constants/support'
import { ROUTES } from 'routes'

/**
 * The PUBLIC support page. The one support surface reachable with no account.
 *
 * ## Why this page exists
 *
 * Both app stores require a **support URL**, and a reviewer clicks it while signed
 * out of everything. Every other support surface in this product is behind
 * `<ProtectedRoute>`: `/settings/my-requests`, `/support/:id` and the `/support/new`
 * intake all resolve company and user from a bearer token, so a logged-out visitor
 * is bounced to `/sign-in` and never sees support at all. The scanner app's own
 * `SUPPORT_URL` pointed at one of them, so "Contact support" in the app led to a
 * login wall for someone who, by construction, has no login (the phone holds no
 * identity — see `apps/vera-app/src/config/api.ts`).
 *
 * ## Why this is a page and not a public intake FORM
 *
 * The obvious move is to expose the existing intake anonymously — `POST
 * /support/app/report` is already `@Public()` and already files against Vera's own
 * `kind='internal'` company, so the backend for it exists today. It was rejected on
 * one specific finding, not on taste:
 *
 *   **An anonymous request has no reply channel.** `support_requests` has no
 *   reporter email, phone or any other contact column (see the table in
 *   `apps/api/src/database/schema/tables.ts`), and `SupportNotifierService`
 *   only ever mails `internalRecipients()` — Vera staff. A reply reaches the
 *   reporter through their in-app THREAD, which requires the `user_id` an
 *   anonymous submission does not have.
 *
 * So an anonymous form would take someone's problem and give them a reference
 * number we could never answer. On a URL printed in two store listings, that is a
 * worse outcome than no form: it looks like support and behaves like a void.
 * Capturing a reply-to address would mean a new column, the internal queue
 * rendering it as a contact, and an outbound mail path to a non-user — a support
 * system for people without accounts, which is a real feature and not a store
 * checkbox.
 *
 * Email is therefore the contact route, and it is NOT an invented address:
 * `support@vera.inc` is `SUPPORT_INTERNAL_EMAIL` in `apps/api/src/config/app.ts`,
 * where every support request already lands. If that mailbox is not monitored,
 * support is already broken for signed-in customers too; this page does not add a
 * dependency, it surfaces one.
 *
 * ## Standalone, outside both route guards
 *
 * Registered outside `ProtectedRoute` AND `UnProtectedRoute` (the same shape as
 * `TRIAL_ENDED`): a signed-IN operator following the app's link must land here too,
 * and `UnProtectedRoute` would redirect them away.
 *
 * It renders no sidebar and makes no API call **of its own**, which matters because
 * someone reaching support is often someone for whom something else is already
 * broken. Verified signed out at `/help`: the app's bootstrap profile check 401s and
 * the page renders anyway, with an empty console. That is the honest version of the
 * claim — the SHELL still does its usual bootstrap; this page does not depend on the
 * result, and nothing here reads company, user or permissions.
 */

// The support inbox moved to `constants/support` on its second site (VRG-595 needed
// it twice more on the signup page). The note about it mirroring the API's
// `SUPPORT_INTERNAL_EMAIL` default moved with it — see that file.

const PRIVACY_URL = 'https://vera.inc/privacy-policy'
const TERMS_URL = 'https://vera.inc/terms-and-conditions'

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack spacing={3}>
    <Typography variant="body2" fontWeight={600}>
      {title}
    </Typography>
    {children}
  </Stack>
)

const Paragraph = ({ children }: { children: ReactNode }) => (
  <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.6 }}>
    {children}
  </Typography>
)

export const PublicHelpPage = () => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 5, sm: 6 },
        py: { xs: 8, sm: 10 },
        backgroundColor: 'background.default',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 680, mx: 'auto' }}>
        <Stack spacing={8}>
          <Stack spacing={4}>
            <Box
              component="img"
              src={logo}
              alt="Vera"
              sx={{ height: 24, width: 'auto', alignSelf: 'flex-start' }}
            />
            <Typography variant="h2">Support</Typography>
            <Paragraph>
              Help with Vera and the Vera app. You do not need an account to read this
              page or to contact us.
            </Paragraph>
          </Stack>

          {/* The contact route leads. Someone who opened this page from a store
              listing or from a dead end in the app wants an address, not a tour. */}
          <Card>
            <Stack spacing={4}>
              <Section title="Contact us">
                <Paragraph>
                  Email{' '}
                  <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ fontWeight: 600 }}>
                    {SUPPORT_EMAIL}
                  </Link>{' '}
                  and tell us what happened. If you were using the Vera app, say which
                  screen you were on and what you expected to see.
                </Paragraph>
              </Section>

              <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
                {/* Plain `href`, not a RouterLink: the shared Button types `href`
                    directly (§47 — the anchor props were added to it rather than
                    worked around per call site), and a full navigation is fine here.
                    This page is normally the FIRST thing a visitor loads, so there is
                    no client-side history to preserve. */}
                <Button variant="primary" disableElevation href={`mailto:${SUPPORT_EMAIL}`}>
                  Email support
                </Button>
                <Button variant="secondary" disableElevation href={ROUTES.SIGN_IN}>
                  Sign in
                </Button>
              </Stack>

              <Paragraph>
                If you already have a Vera account, signing in gives you more than
                email does. Open Help, then Contact support, and your request gets a
                reference number and a thread you can follow the reply in.
              </Paragraph>
            </Stack>
          </Card>

          <Section title="What Vera is">
            <Paragraph>
              Vera connects physical products to digital certificates. A brand attaches
              a Vera tag to a product. Tapping or scanning that tag with a phone opens
              the product's certificate, which shows what the product is, who owns it,
              and its history.
            </Paragraph>
            <Paragraph>
              Vera is sold to brands. If you have scanned a tag as a customer and
              something looks wrong, contact the brand you bought from first. They own
              the product data on the certificate. Write to us if you cannot reach them.
            </Paragraph>
          </Section>

          <Section title="What the Vera app does">
            <Paragraph>
              The Vera app is the tool brand staff use to put certificates onto
              products. It is a companion to a Vera account, not a standalone app, and
              it has no sign-in of its own.
            </Paragraph>
            <Paragraph>
              A scanning session is started from the Vera console, not from the phone.
              Open a product, choose Create Certificates, then scan the QR code shown on
              screen with the app. Tags you then hold the phone against are added to that
              session automatically.
            </Paragraph>
            <Paragraph>
              This means the app has nothing to do until someone starts a session for it
              and has Vera tags to hand. That is expected behaviour, not a fault.
            </Paragraph>
          </Section>

          <Section title="Common problems">
            <Stack spacing={3}>
              <Paragraph>
                <b>The app asks me to start a session from the platform.</b> That is the
                app working correctly. Sessions are created in the Vera console, and the
                app joins one by scanning its QR code.
              </Paragraph>
              <Paragraph>
                <b>A tag will not scan.</b> Hold the phone still and in contact with the
                tag. Reading a tag over NFC needs the phone within a few centimetres, and
                a case or a metal surface can block it.
              </Paragraph>
              <Paragraph>
                <b>Something is broken and I want to report it.</b> You can file a report
                from inside the app without an account. Open Help, then Report a problem.
                It reaches the same place email does and gives you a reference number.
              </Paragraph>
            </Stack>
          </Section>

          <Box sx={{ pt: 2, borderTop: `1px solid ${theme.palette.primary[100]}` }}>
            <Stack direction="row" spacing={4} sx={{ pt: 4, flexWrap: 'wrap' }}>
              <Link href={PRIVACY_URL} variant="body4" color="text.secondary">
                Privacy policy
              </Link>
              <Link href={TERMS_URL} variant="body4" color="text.secondary">
                Terms
              </Link>
              <Link href="https://vera.inc" variant="body4" color="text.secondary">
                vera.inc
              </Link>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  )
}
